import { type ResolvedValue } from '@databases/selections-api-types';
import { DatabaseFieldType, type DatabaseValue } from '@databases/types/database';
import { isEqual } from 'lodash-es';

const getDatePart = (value: ResolvedValue['value']) =>
  value?.type === DatabaseFieldType.DATE && typeof value.value === 'string'
    ? value.value.slice(0, 10)
    : undefined;

const getTimePart = (value: ResolvedValue['value']) =>
  value?.type === DatabaseFieldType.DATE && typeof value.value === 'string'
    ? value.value.match(/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}.*)$/)?.[1] ?? ''
    : undefined;

/**
 * Computes the initial value for bulk editing.
 * Returns the common value only when a single field is selected and all values are equal.
 */
export function getInitialValueForBulkEdit(
  selectedValues: readonly ResolvedValue[],
  fieldsWithSelectionsCount: number
) {
  if (fieldsWithSelectionsCount !== 1 || selectedValues.length === 0) {
    return undefined;
  }

  const firstValue = selectedValues[0].value;
  const allEqual = selectedValues.every((v) => isEqual(v.value, firstValue));

  if (!allEqual) {
    const firstField = selectedValues[0].field;
    const firstDatePart = getDatePart(firstValue);
    const firstTimePart = getTimePart(firstValue);
    const allDatePartsEqual = selectedValues.every(
      (selectedValue) => getDatePart(selectedValue.value) === firstDatePart
    );
    const allTimePartsEqual = selectedValues.every(
      (selectedValue) => getTimePart(selectedValue.value) === firstTimePart
    );

    if (
      firstField.type === DatabaseFieldType.DATE &&
      firstField.settings.timeFormat &&
      firstDatePart &&
      allDatePartsEqual &&
      !allTimePartsEqual
    ) {
      const dateValue: DatabaseValue<DatabaseFieldType.DATE> = {
        type: DatabaseFieldType.DATE,
        value: firstDatePart,
      };

      return dateValue;
    }
  }

  return allEqual ? firstValue : undefined;
}
